import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { getDefaultBannerPriceData } from '../../service/BannerHelpers/BannerHelpers';
import { Bem } from '../../service/Bem/Bem';
import { BannerPrice } from '../../typings/banners';
import { CellKeyFields } from '../../typings/common';
import { AdvType } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import { GridEditCellStatus } from '../../typings/grid';
import { BannerPriceEditPopup } from '../BannerPriceEditPopup/BannerPriceEditPopup';
import { BannerPriceInfo } from '../BannerPriceInfo/BannerPriceInfo';
import { EditableCell } from '../EditableCell/EditableCell';
import { ErrorItem } from '../ErrorsPresenter/ErrorsPresenter';

import './GridBannerPriceCell.css';
import * as keyset from './GridBannerPriceCell.i18n';

const i18n = i18nFactory(keyset);

export interface GridBannerPriceCellProps {
    bannerPrice?: BannerPrice;
    bannerType: AdvType;
    isEditable: boolean;
    isGridScrolling: boolean;
    isEditInProgress?: boolean;
    updateBannerPrice: (bannerPrice: BannerPrice) => void;
    isSaveInProgress?: boolean;
    errors: ErrorItem[];
    onClearErrors: (
        cellKeysFields: CellKeyFields,
        data: GridEditCellStatus
    ) => void;
    cellKeysFields: CellKeyFields;
    workCurrency: CurrencyCode;
}
interface GridBannerPriceCellState {
    isBannerPriceEditPopupOpened: boolean;
}

const BLOCK = 'grid-banner-price-cell';

export class GridBannerPriceCell extends React.PureComponent<
    GridBannerPriceCellProps,
    GridBannerPriceCellState
> {
    private ref: HTMLElement;

    static getDerivedStateFromProps(nextProps: GridBannerPriceCellProps) {
        if (nextProps.isGridScrolling) {
            return {
                isBannerPriceEditPopupOpened: false
            };
        }

        return null;
    }

    componentDidUpdate(prevProps: GridBannerPriceCellProps) {
        if (
            (prevProps.isSaveInProgress !== this.props.isSaveInProgress ||
                prevProps.errors !== this.props.errors ||
                prevProps.isEditInProgress !== this.props.isEditInProgress) &&
            !this.props.isSaveInProgress &&
            !this.props.errors.length &&
            !this.props.isEditInProgress
        ) {
            this.setState({
                isBannerPriceEditPopupOpened: false
            });
        }
    }

    constructor(props: GridBannerPriceCellProps) {
        super(props);

        this.state = {
            isBannerPriceEditPopupOpened: false
        };
    }

    render() {
        return (
            <div className={Bem.cls(BLOCK)}>
                {this.props.bannerType === AdvType.TEXT
                    ? this.getPrice()
                    : this.getDash()}
            </div>
        );
    }

    private getDash = () => {
        return <div className={Bem.cls(BLOCK, 'dash')}>{'–'}</div>;
    };

    private getPrice = () => {
        return this.props.isEditable ? (
            <div
                className={Bem.cls(BLOCK, 'info', { editable: 'yes' })}
                ref={this.createRef}
            >
                <EditableCell
                    onClick={this.openPricePopup}
                    isActive={false}
                    cls={Bem.cls('editable-cell__inline-block')}
                >
                    {this.getPriceInfo()}
                </EditableCell>
                {this.getPopup()}
            </div>
        ) : (
            <div className={Bem.cls(BLOCK, 'info', { editable: 'no' })}>
                {this.getPriceInfo()}
            </div>
        );
    };

    private getPriceInfo = () => {
        const bannerPrice = this.props.bannerPrice;

        return bannerPrice && bannerPrice.price ? (
            <BannerPriceInfo bannerPrice={bannerPrice} isColumn={true} />
        ) : (
            <span>{i18n('emptyPrice')}</span>
        );
    };

    private openPricePopup = () => {
        this.setState({ isBannerPriceEditPopupOpened: true });
        this.onClearErrors(true);
    };

    private onCancelEditing = () => {
        this.setState({
            isBannerPriceEditPopupOpened: false
        });
    };

    private getPopup = () => {
        const { isBannerPriceEditPopupOpened } = this.state;
        const {
            bannerPrice,
            updateBannerPrice,
            isSaveInProgress,
            errors,
            workCurrency
        } = this.props;

        if (!isBannerPriceEditPopupOpened) {
            return null;
        }

        const bannerPriceData =
            bannerPrice && bannerPrice.price
                ? bannerPrice
                : getDefaultBannerPriceData(workCurrency);

        return (
            <BannerPriceEditPopup
                workCurrency={workCurrency}
                onCancel={this.onCancelEditing}
                bannerPrice={bannerPriceData}
                onSave={updateBannerPrice}
                anchor={this.ref}
                isVisible={isBannerPriceEditPopupOpened}
                isSaveInProgress={isSaveInProgress}
                errors={errors}
                onClearErrors={this.onClearErrors}
            />
        );
    };

    private onClearErrors = (isEditInProgress: boolean) => {
        const { errors, cellKeysFields, onClearErrors } = this.props;
        const data = {
            isEditInProgress: isEditInProgress,
            isSaving: false,
            error: []
        };

        if (errors.length) {
            onClearErrors(cellKeysFields, data);
        }

        return;
    };

    private createRef = (ref: HTMLDivElement) => {
        this.ref = ref;
    };
}
